import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/cart-shopping-carts/my-cart/item/{uuid}/{session_id}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetCartItemDetailInput = paths[url]['get']['parameters']['path'];
export type GetCartItemDetailSuccessResponse = paths[url]['get']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to get the detail of an item in a shopping cart.
 * Then saves it in Data Context.
 */
export const useGetCartItemDetail = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const { login, setCartItemDetail } = useData();
    const [loading, setLoading] = useState<boolean>(false);

    const sessionId = getSessionId();

    const request = async (props: Omit<GetCartItemDetailInput, 'session_id'>) => {
        setLoading(true);

        try {
            const response = await fetch(
                `${API_URL}${ENDPOINT_URL.replace('/{session_id}', login === null ? '/' + sessionId : '').replace('{uuid}', props.uuid)}`,
                {
                    method: 'GET',
                    headers: getHeaders(),
                }
            );

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetCartItemDetailSuccessResponse = await response.json();

            setCartItemDetail(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
        } finally {
            setLoading(false);
        }
    };

    return { request, loading };
};
